import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { HomeShell } from "@/components/HomeShell";
import { ToolCard } from "@/components/ToolCard";
import { ToolLogo } from "@/components/ToolLogo";
import { NewsFeed } from "@/components/NewsFeed";
import { Wikifi } from "@/components/Wikifi";
import { HomeSections } from "@/components/HomeSections";
import { PopularToolsRow } from "@/components/PopularToolsRow";
import { HomepageSearchBar } from "@/components/HomepageSearchBar";
import { CategoryTabs } from "@/components/CategoryTabs";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORIES, categoryTestId } from "@/lib/categories";
import { Link } from "react-router-dom";
import { Zap, MessageSquare } from "lucide-react";

/** Eased count-up that animates ONCE per mount, on the first non-null value.
 *  Subsequent updates (e.g. background refreshes every 60s) just update the
 *  display value immediately without re-animating — fixes the "counter shows
 *  random numbers on every load" bug caused by re-animation. */
function CountUp({ value }) {
  const [display, setDisplay] = useState(0);
  const animatedRef = useRef(false);

  useEffect(() => {
    if (value == null) return;
    if (animatedRef.current) {
      // Silent update on subsequent refreshes — no re-animation.
      setDisplay(value);
      return;
    }
    animatedRef.current = true;
    const start = performance.now();
    const dur = 1400;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return <>{display.toLocaleString()}</>;
}

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const [tools, setTools] = useState(null);
  const [trending, setTrending] = useState([]);
  const [notable, setNotable] = useState([]);
  const [news, setNews] = useState([]);
  const [compare, setCompare] = useState([]);
  const [sort, setSort] = useState("rank");
  const [free, setFree] = useState(false);
  const [hidden, setHidden] = useState(params.get("hidden") === "1");
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(parseInt(params.get("page") || "1", 10));
  const [pageMeta, setPageMeta] = useState({ total: 0, pages: 1 });
  const PER_PAGE = 40;
  const { user } = useAuth();

  const cat = params.get("cat") || null;
  const search = params.get("search") || null;

  // Monotonic request id — every fetch bumps this ref, and any resolved
  // response that isn't the *latest* is discarded. Fixes CR-003: changing
  // a filter while on page > 1 used to fire two fetches (old-page then
  // page-1) and whichever resolved last would win.
  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = new URLSearchParams();
    if (cat) q.set("category", cat);
    if (search) q.set("search", search);
    if (free) q.set("free_tier", "true");
    if (hidden) q.set("hidden_gem", "true");
    q.set("sort", sort);
    q.set("page", String(page));
    q.set("per_page", String(PER_PAGE));

    const myId = ++reqIdRef.current;
    setTools(null);
    api.get(`/tools?${q.toString()}`).then(r => {
      // Discard if a newer fetch has already been issued.
      if (myId !== reqIdRef.current) return;
      if (Array.isArray(r.data)) {
        setTools(r.data);
        setPageMeta({ total: r.data.length, pages: 1 });
      } else {
        setTools(r.data.items || []);
        setPageMeta({ total: r.data.total || 0, pages: r.data.pages || 1 });
      }
    }).catch(() => {
      if (myId !== reqIdRef.current) return;
      setTools([]);
      setPageMeta({ total: 0, pages: 1 });
    });
  }, [cat, search, free, hidden, sort, page]);

  // Reset to page 1 whenever filters/sort change (but not when page itself changes)
  useEffect(() => { setPage(1); }, [cat, search, free, hidden, sort]);

  useEffect(() => {
    api.get("/trending").then(r => setTrending(r.data));
    api.get("/new-notable").then(r => setNotable(r.data.slice(0, 4)));
    api.get("/news").then(r => setNews(r.data.items || [])).catch(() => setNews([]));
    const loadStats = () => api.get("/stats").then(r => setStats(r.data)).catch(() => {});
    loadStats();
    const iv = setInterval(loadStats, 60 * 1000); // keep the live counter fresh
    return () => clearInterval(iv);
  }, []);

  const favs = useMemo(()=> new Set(user?.favorites || []), [user]);
  const pinned = useMemo(()=> new Set(user?.stack || []), [user]);

  const toggleCompare = (t) => setCompare(c =>
    c.find(x => x.slug === t.slug) ? c.filter(x => x.slug !== t.slug) : c.length >= 4 ? c : [...c, t]
  );

  // Decode HTML entities (e.g. `&#8217;` → `'`) so news headlines read cleanly.
  const decodeEntities = (s) => {
    if (typeof s !== "string" || s.indexOf("&") < 0) return s || "";
    // DOM-based decode — safe because we only read `textContent`, never innerHTML.
    const el = document.createElement("textarea");
    el.innerHTML = s;
    return el.value;
  };
  // Pretty pill labels for the Breakthroughs mini-feed. Cycles NEW→HOT→AI
  // in order of recency so the top item always gets the highlight treatment.
  const BREAKTHROUGH_PILLS = ["NEW", "HOT", "AI"];
  const timeAgo = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "";
    const d = Math.max(0, Date.now() - t);
    const h = Math.floor(d / 3.6e6);
    if (h < 1) return "just now";
    if (h < 24) return `${h}h ago`;
    const dd = Math.floor(h / 24);
    return dd < 7 ? `${dd}d ago` : `${Math.floor(dd / 7)}w ago`;
  };

  // Deterministic source-color for the news item icon (matches the design in
  // the reference HTML — orange squares for crypto, indigo for AI publications).
  const sourceIcon = (n) => {
    const isCrypto = n.kind === "crypto";
    const bg = isCrypto ? "#f59e0b" : "#6366f1";
    const src = (n.source || n.domain || "").trim();
    const letter = src ? src[0].toUpperCase() : (isCrypto ? "₿" : "AI");
    return { bg, letter };
  };

  const rightPanel = (
    <div className="relative h-full min-h-[720px]" data-testid="livefeed-column">
      {/* ── Breakthroughs (top of the right panel) ───────────────── */}
      <div className="mb-5" data-testid="breakthroughs-section">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-[#F97316] fill-[#F97316]"/>
          <h3 className="text-[13px] font-bold text-[#0B1220] tracking-tight">Breakthroughs</h3>
        </div>
        {news.length > 0 ? (
          <div className="space-y-2">
            {news.slice(0, 3).map((n, i) => {
              const pill = BREAKTHROUGH_PILLS[i] || "AI";
              // NEW → light-orange bg + brick text · HOT → dark bg + white · AI → indigo tint + indigo text
              const pillCls =
                pill === "NEW" ? "bg-[#ffedd5] text-[#c2410c]" :
                pill === "HOT" ? "bg-[#1e293b] text-white" :
                                 "bg-[#e0e7ff] text-[#4338ca]";
              return (
                <a
                  key={n.link || i}
                  href={n.link || "#"}
                  target={n.link ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="block rounded-[10px] border border-[rgba(15,23,42,0.06)] bg-[#fafbfc] hover:bg-white hover:border-[rgba(15,23,42,0.12)] transition-all px-3 py-2.5 group"
                  data-testid={`breakthrough-${i}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${pillCls}`}>
                      {pill}
                    </span>
                    <span className="text-[10.5px] text-[#64748b]">{timeAgo(n.published)}</span>
                  </div>
                  <div className="text-[12.5px] leading-[1.4] font-semibold text-[#0f172a] line-clamp-2 group-hover:text-[#0a0a0b]">
                    {decodeEntities(n.title)}
                  </div>
                </a>
              );
            })}
          </div>
        ) : (
          <div className="text-[11px] text-[#8A93A6] italic">Loading latest…</div>
        )}
      </div>

      {/* ── News header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#0B1220]">
          <span className="live-dot w-[7px] h-[7px] rounded-full bg-[#22c55e] flex-shrink-0"/>
          News
        </div>
        <span className="text-[10px] font-semibold text-[#64748b] uppercase tracking-[0.06em]">Auto</span>
      </div>

      {/* ── News list (colored favicon squares + CRYPTO/AI tag + source · time) ── */}
      <div className="flex flex-col gap-0.5">
        {news.slice(3, 3 + 6).map((n, i) => {
          const { bg, letter } = sourceIcon(n);
          const tagCls = n.kind === "crypto"
            ? "bg-[#fef3c7] text-[#b45309]"
            : "bg-[#e0e7ff] text-[#4338ca]";
          return (
            <a
              key={n.link || i}
              href={n.link || "#"}
              target={n.link ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="flex gap-2.5 px-1.5 py-2 rounded-[9px] hover:bg-[#f8fafc] transition-colors group"
              data-testid={`news-item-${i}`}
            >
              <div
                className="w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: bg }}
                aria-hidden="true"
              >
                {letter}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] leading-[1.35] font-medium text-[#0f172a] line-clamp-2 mb-1">
                  {decodeEntities(n.title)}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-[1px] rounded-[3px] ${tagCls}`}>
                    {n.kind === "crypto" ? "Crypto" : "AI"}
                  </span>
                  <span className="text-[10.5px] text-[#64748b]">
                    {n.source || n.domain}{n.published ? ` · ${timeAgo(n.published)}` : ""}
                  </span>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );

  return (
    <HomeShell
      rightPanel={rightPanel}
      activeCategory={cat}
      belowWindow={<HomeSections stats={stats} />}
      topStrip={
        <>
          <HomepageSearchBar />
          <PopularToolsRow />
        </>
      }
    >
      {/* --- Hero (shared PageHero component, bare variant — flush with page) --- */}
      <div className="relative mb-8" data-testid="hero-with-search">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(rgba(11,18,32,0.10) 1px, transparent 1.2px)",
            backgroundSize: "24px 24px",
            maskImage:
              "radial-gradient(ellipse 85% 95% at 78% 45%, #000 30%, rgba(0,0,0,0.45) 65%, transparent 90%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 85% 95% at 78% 45%, #000 30%, rgba(0,0,0,0.45) 65%, transparent 90%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 80% at 92% 20%, rgba(249, 115, 22, 0.06), transparent 65%)",
          }}
        />
        <PageHero
          testId="home-hero"
          variant="bare"
          eyebrow="Wikificial Directory"
          title="The Encyclopedia of AI"
          sub={`Search ${stats?.total_tools ? stats.total_tools.toLocaleString() : "28,656"}+ tools — scored, compared, and ranked so you don't have to guess.`}
        >
          {/* Stats — three neutral pills using the site's #F7F8FA / #E7EAF0 tokens. */}
          <div className="flex flex-wrap gap-2" data-testid="hero-stats">
            <div
              className="px-3 py-1.5 rounded-full bg-[#F7F8FA] border border-[#E7EAF0] text-[12px] text-[#5B6478]"
              data-testid="hero-stat-tools"
            >
              <strong className="text-[#0B1220] font-semibold tabular-nums">
                {stats?.total_tools ? stats.total_tools.toLocaleString() + "+" : "…"}
              </strong>{" "}
              tools
            </div>
            <div
              className="px-3 py-1.5 rounded-full bg-[#F7F8FA] border border-[#E7EAF0] text-[12px] text-[#5B6478]"
              data-testid="hero-stat-categories"
            >
              <strong className="text-[#0B1220] font-semibold tabular-nums">
                {stats?.categories_covered ?? "…"}
              </strong>{" "}
              categories
            </div>
            <div
              className="px-3 py-1.5 rounded-full bg-[#F7F8FA] border border-[#E7EAF0] text-[12px] text-[#5B6478]"
              data-testid="hero-stat-grades"
            >
              Dual scores + letter grades
            </div>
          </div>
        </PageHero>
      </div>

      {/* --- Category tabs (shared component — same as /prompts).
           Curated 9-item list per product spec — deliberately shorter than
           the full CATEGORIES roster in lib/categories.js so the row stays
           on one line and only surfaces the biggest AI verticals. Do NOT
           auto-generate from CATEGORIES — the extra items would visually
           overflow the row (e.g. "Automation & Workflows" showing up right
           after "No-Code & Vibe Coding"). */}
      <CategoryTabs
        tabs={[
          { id: "__all__",              label: "All" },
          { id: "Chat & LLMs",          label: "Chat & LLMs" },
          { id: "AI Agents",            label: "AI Agents" },
          { id: "Image",                label: "Image" },
          { id: "Video",                label: "Video" },
          { id: "Music & Audio",        label: "Music & Audio" },
          { id: "Voice",                label: "Voice" },
          { id: "Writing & Copy",       label: "Writing & Copy" },
          { id: "No-Code & Vibe Coding",label: "No-Code & Vibe Coding" },
        ]}
        value={cat || "__all__"}
        onChange={(id) => {
          if (id === "__all__") { params.delete("cat"); }
          else { params.set("cat", id); }
          setParams(params);
        }}
        testIdPrefix="cat-tab"
      />

      {/* --- Filter chips (compact, inline) --- */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="filter-bar">
        {cat && (
          <button
            onClick={() => { params.delete("cat"); setParams(params); }}
            className="text-[11px] px-2.5 py-1 rounded-full bg-[#0B1220] text-white"
          >{cat} ✕</button>
        )}
        {search && (
          <button
            onClick={() => { params.delete("search"); setParams(params); }}
            className="text-[11px] px-2.5 py-1 rounded-full bg-[#0B1220] text-white"
          >"{search}" ✕</button>
        )}
        <label className="text-[11px] flex items-center gap-1.5 cursor-pointer text-[#5B6478]" data-testid="filter-free">
          <input type="checkbox" checked={free} onChange={(e) => setFree(e.target.checked)} className="accent-[#F97316]" /> Has Free Tier
        </label>
        <label className="text-[11px] flex items-center gap-1.5 cursor-pointer text-[#5B6478]" data-testid="filter-hidden">
          <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} className="accent-[#F97316]" /> Hidden Gem
        </label>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-white border border-[rgba(210,218,232,0.9)] focus:outline-none focus:border-[#0B1220]/40"
          data-testid="sort-select"
        >
          <option value="rank">Rank</option>
          <option value="curio_score">Wikifi Score</option>
          <option value="user_score">User Score</option>
          <option value="trending">Trending</option>
          <option value="name">A→Z</option>
        </select>
      </div>

      {/* --- Tool cards grid --- */}
      {!tools ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
          {[...Array(12)].map((_, i) => <div key={`skl-${i}`} className="skeleton h-52" />)}
        </div>
      ) : tools.length === 0 ? (
        <div className="text-center py-16 text-[#5B6478]" data-testid="empty-state">
          <div className="text-2xl font-display italic mb-2">No tools found.</div>
          <div className="text-sm">Try clearing filters or a different search.</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5 stagger" data-testid="tools-grid">
            {tools.map((t) => (
              <ToolCard
                key={t.slug}
                tool={t}
                onCompareToggle={toggleCompare}
                isCompared={!!compare.find(c => c.slug === t.slug)}
                isFav={favs.has(t.slug)}
                isPinned={pinned.has(t.slug)}
                compact
              />
            ))}
          </div>

          {pageMeta.pages > 1 && (
            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap" data-testid="pagination">
              <div className="text-[11px] text-[#8A93A6]">
                Page <span className="font-mono font-bold text-[#0B1220]">{page}</span> of <span className="font-mono">{pageMeta.pages}</span>
                · <span className="font-mono">{pageMeta.total.toLocaleString()}</span> tools
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[rgba(210,218,232,0.9)] bg-white disabled:opacity-40 hover:bg-[#F5F7FB] transition-colors"
                  data-testid="page-prev"
                >‹ Prev</button>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const n = parseInt(e.currentTarget.jump.value, 10);
                    if (Number.isFinite(n)) setPage(Math.min(pageMeta.pages, Math.max(1, n)));
                  }}
                  className="flex items-center gap-1"
                  data-testid="jump-form"
                >
                  <input
                    name="jump"
                    type="number"
                    min={1}
                    max={pageMeta.pages}
                    placeholder="Jump to…"
                    className="text-[11px] w-20 px-2 py-1 rounded-full border border-[rgba(210,218,232,0.9)] bg-white focus:outline-none focus:border-[#0B1220]/40"
                    data-testid="page-jump-input"
                    aria-label={`Jump to page (1 to ${pageMeta.pages})`}
                  />
                  <button
                    type="submit"
                    className="text-[11px] px-2.5 py-1 rounded-full bg-[#0B1220] text-white hover:opacity-90 transition-opacity"
                    data-testid="page-jump-btn"
                  >Go</button>
                </form>
                <button
                  onClick={() => setPage(p => Math.min(pageMeta.pages, p + 1))}
                  disabled={page >= pageMeta.pages}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[rgba(210,218,232,0.9)] bg-white disabled:opacity-40 hover:bg-[#F5F7FB] transition-colors"
                  data-testid="page-next"
                >Next ›</button>
              </div>
            </div>
          )}
        </>
      )}
    </HomeShell>
  );
}
